import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMonths, firstOfMonth, todayIn } from '@njpos/core';
import { api, monthLabel, sizeLabel, usd, usd0 } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

export default function BuyList() {
  const nav = useNavigate();
  const thisMonth = firstOfMonth(todayIn());
  const [month, setMonth] = useState(addMonths(thisMonth, 1));
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<any>(null);
  const [onlyDeals, setOnlyDeals] = useState(true);
  const recs = useLoad(() => api<any[]>(`/api/deals?month=${month}`), [month]);
  const files = useLoad(() => api<any[]>(`/api/cpl/files?month=${month}`), [month]);
  const [mix, setMix] = useState<any[]>([]);

  async function run() {
    setRunning(true); setErr(null);
    try { const r = await api<any>('/api/deals/run', { body: { saleMonth: month } }); setMix(r.mixGroups); recs.reload(); setPicked(new Set()); }
    catch (e) { setErr(e); } finally { setRunning(false); }
  }
  async function order() {
    setErr(null);
    try { await api('/api/purchase-orders/from-recommendations', { body: { recommendationIds: [...picked] } }); nav('/orders'); }
    catch (e) { setErr(e); }
  }

  const rows = (recs.data ?? []).filter((r) => r.status === 'open' && (!onlyDeals || r.best.cases > r.baseline.cases || r.best.ripCents > 0));
  const totalGain = rows.filter((r) => picked.has(r.id)).reduce((s, r) => s + r.best.gainCents, 0);
  const totalInvoice = rows.filter((r) => picked.has(r.id)).reduce((s, r) => s + r.best.invoiceCents, 0);
  const opportunity = rows.reduce((s, r) => s + Math.max(0, r.best.gainCents), 0);

  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow">
          <h1>Buy list for {monthLabel(month)}</h1>
          <p>Every vendor’s posted prices, quantity discounts and RIPs, matched to what your store actually sells. Extra profit is after the cost of holding the stock.</p>
        </div>
        <select aria-label="Month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }}>
          {[thisMonth, addMonths(thisMonth, 1)].map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <button className="primary" onClick={run} disabled={running}>{running ? 'Working…' : recs.data?.length ? 'Recalculate' : 'Build buy list'}</button>
      </div>
      <ErrorNote error={err ?? recs.error} />
      {files.data && files.data.length === 0 && <div className="notice warn">No price lists are loaded for {monthLabel(month)} yet. They’re published around the 18th of the month before.</div>}
      {files.data && files.data.length > 0 && (
        <p className="faint">Price lists loaded: {files.data.map((f) => `${f.wholesaler} (${f.matched}/${f.line_count} matched)`).join('; ')}</p>
      )}
      {mix.filter((g) => g.casesShort > 0 && g.casesShort <= 3).map((g) => (
        <div key={g.programId} className="notice warn">{g.wholesaler} mix RIP {g.code}: {g.casesShort} more case{g.casesShort > 1 ? 's' : ''} of {g.items.join(', ')} reaches the {usd(g.nextTier.amountCents)} tier.</div>
      ))}

      <div className="stats">
        <div><div className="v">{usd0(opportunity)}</div><div className="l">Extra profit on this list</div></div>
        <div><div className="v">{rows.length}</div><div className="l">Items with a deal worth taking</div></div>
        <div><div className="v">{usd0(totalInvoice)}</div><div className="l">Invoices for what you picked</div></div>
        <div><div className="v">{usd0(totalGain)}</div><div className="l">Extra profit picked</div></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}><input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={onlyDeals} onChange={(e) => setOnlyDeals(e.target.checked)} /> Only show items where a deal changes what to buy</label>
          <span className="grow" />
          <button className="primary" disabled={picked.size === 0} onClick={order}>Create purchase orders ({picked.size})</button>
        </div>
        {recs.loading ? <div className="empty">Loading…</div> : rows.length === 0 ? (
          <div className="empty">{recs.data?.length ? 'No deals beat buying what you need this month.' : 'Build the buy list to see what to order.'}</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr>
                <th><input type="checkbox" aria-label="Pick all" style={{ width: 'auto', minHeight: 0 }} checked={picked.size === rows.length} onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>
                <th>Product</th><th className="hide-sm">Vendor</th><th className="n">Buy</th><th className="n">Net / bottle</th><th className="n hide-sm">Shelf</th><th className="n hide-sm">Stock covers</th><th className="n">Extra profit</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><input type="checkbox" aria-label={`Pick ${r.product}`} style={{ width: 'auto', minHeight: 0 }} checked={picked.has(r.id)} onChange={(e) => { const p = new Set(picked); if (e.target.checked) p.add(r.id); else p.delete(r.id); setPicked(p); }} /></td>
                    <td><div style={{ fontWeight: 600 }}>{r.product}</div><div className="faint">{sizeLabel(r.sizeMl)} · on hand {r.onHand}{r.incoming ? ` + ${r.incoming} coming` : ''}</div><div className="faint show-sm">{r.wholesaler}</div>
                      {r.advice.filter((a: any) => a.code !== 'CASH_GAP' || r.best.cashGapCents > r.best.invoiceCents / 2).map((a: any) => <div key={a.code} className={`chip ${a.code === 'RETAIL_BELOW_LEGAL_COST' ? 'bad' : a.code === 'NEAR_RIP_TIER' ? 'amber' : ''}`} title={a.message}>{a.code === 'NEAR_RIP_TIER' ? '1 case from a RIP' : a.code === 'RETAIL_BELOW_LEGAL_COST' ? 'Raise shelf price' : a.code === 'CASH_GAP' ? 'Cash ahead of sales' : a.code === 'HAS_ENOUGH_STOCK' ? 'Stocked' : 'No sales'}</div>)}
                    </td>
                    <td className="hide-sm">{r.wholesaler}{r.rip && <div className="faint">RIP {r.rip.code}{r.rip.mixGroup ? ' (mix)' : ''}</div>}</td>
                    <td className="n"><div className="buy-qty">{r.best.cases} cs</div><div className="buy-was">need {r.baseline.cases}</div></td>
                    <td className="n">{usd(Math.round(r.best.netUnitCents))}<div className="buy-was">legal cost {usd(r.best.legalUnitCents)}</div></td>
                    <td className="n hide-sm">{usd(r.retailCents)}</td>
                    <td className="n hide-sm">{Number.isFinite(r.best.monthsCover) ? `${r.best.monthsCover} mo` : '—'}</td>
                    <td className="n"><span className={`gain${r.best.gainCents <= 0 ? ' zero' : ''}`}>{r.best.gainCents > 0 ? `+${usd0(r.best.gainCents)}` : '$0'}</span></td>
                    <td><button className="ghost" onClick={() => setDetail(r)}>Why</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detail && <Why rec={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Why({ rec, onClose }: { rec: any; onClose: () => void }) {
  return (
    <Modal wide title={`${rec.product} ${sizeLabel(rec.sizeMl)}`} onClose={onClose}>
      <p className="muted">Forecast for the next months (bottles): {rec.forecast.map((f: number) => Math.round(f)).join(' · ')} {rec.forecastMethod === 'insufficient_history' ? '(little sales history yet)' : ''}</p>
      <table className="grid">
        <thead><tr><th></th><th className="n">Cases</th><th className="n">Invoice</th><th className="n">RIP check</th><th className="n">Net / bottle</th><th className="n">Savings</th><th className="n">Holding cost</th><th className="n">Extra profit</th></tr></thead>
        <tbody>
          {[['Buy what you need', rec.baseline], ['Recommended', rec.best]].map(([label, c]: any) => (
            <tr key={label}><td>{label}</td><td className="n">{c.cases}</td><td className="n">{usd(c.invoiceCents)}</td><td className="n">{usd(c.ripCents)}</td><td className="n">{usd(Math.round(c.netUnitCents))}</td><td className="n">{usd(c.savingsCents)}</td><td className="n">{usd(c.holdingCents)}</td><td className="n">{usd(c.gainCents)}</td></tr>
          ))}
        </tbody>
      </table>
      {rec.advice.map((a: any) => <div key={a.code} className="notice">{a.message}</div>)}
      <p className="faint">Savings compare buying now at the deal price with buying the extra later at the frontline price. RIP checks arrive 30–90 days after you pay and never lower the legal cost used for shelf pricing.</p>
      {rec.alternatives?.length > 0 && <p className="faint">Also offered by: {rec.alternatives.map((a: any) => `${a.wholesaler} at ${usd(a.frontlineCaseCents)}/case`).join('; ')}</p>}
    </Modal>
  );
}
